import { useEffect, useContext, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { SessionContext } from "../context/SessionContext";
import { ProfileContext } from "../context/ProfileContext";
import { toast } from "react-hot-toast";

import ToastDisplay from "../components/ToastDisplay";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Pagination from "../components/Pagination";
import PageLoader from "../components/PageLoader";
import Empty from "../components/Empty";

const Users = () => {
	const navigate = useNavigate();
	const photoRef = useRef(null);
	const [session] = useContext(SessionContext);
	const [profile] = useContext(ProfileContext);
	const [users, setUsers] = useState([]);
	const [meta, setMeta] = useState(null);
	const [searchParams, setSearchParams] = useSearchParams({});
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState(false);
	const [filtermodal, setFilterModal] = useState(false);
	const [showFilters, setShowFilters] = useState(false);
	const [userModal, setUserModal] = useState(false);
	const [errors, setErrors] = useState([]);
	const [roles, setRoles] = useState([]);

	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [role, setRole] = useState("");

	const [searchQ, setSearchQ] = useState(searchParams.get("q") || "");
	const [searchRole, setSearchRole] = useState(searchParams.get("role") || "");
	const [searchStatus, setSearchStatus] = useState(
		searchParams.get("status") || ""
	);

	const searchFields = [
		{ key: "q", value: searchQ },
		{ key: "role", value: searchRole },
		{ key: "status", value: searchStatus },
	];

	const filterFields = [
		{
			key: "role",
			value: searchRole,
			defaultVal: "",
			edit: setSearchRole,
		},
		{
			key: "status",
			value: searchStatus,
			defaultVal: "",
			edit: setSearchStatus,
		},
	];

	const requiredFields = [
		{ key: "name", value: name },
		{ key: "email", value: email },
		{ key: "role", value: role },
	];

	const getData = async () => {
		setLoading(true);
		const requestOptions = {
			method: "GET",
			headers: { "X-Auth-Token": session },
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/users`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content="Something went wrong." />);
		} else {
			setUsers(data.users);
			setMeta(data.meta);
			setRoles(data.roles);
		}
		setLoading(false);
	};

	const search = (e) => {
		e.preventDefault();
		var newSearch = { ...searchParams };
		searchFields.map((field) => {
			if (field.value !== "") {
				newSearch[field.key] = field.value;
			} else {
				delete newSearch[field.key];
			}
		});
		setSearchParams(newSearch);
		setFilterModal(false);
	};

	const removeFilter = (e, func, key) => {
		e.preventDefault();
		func("");
		var newSearch = { ...searchParams };
		searchFields.map((field) => {
			if (field.value !== "" && field.key !== key) {
				newSearch[field.key] = field.value;
			} else {
				delete newSearch[field.key];
			}
		});
		setSearchParams(newSearch);
	};

	const clearFilters = (e) => {
		e.preventDefault();
		setSearchParams({});
		filterFields.map((field) => {
			field.edit(field.defaultVal);
		});
	};

	const closeUserModal = () => {
		setUserModal(false);
		setName("");
		setEmail("");
		setRole("");
		setErrors([]);
	};

	const isEmpty = (value) => {
		return value === "" || value === null || value === undefined;
	};

	const saveUser = async () => {
		const requestOptions = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Auth-Token": session,
			},
			body: JSON.stringify({
				name: name,
				email: email,
				role: role,
			}),
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/users/`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(
				<ToastDisplay
					status="error"
					content={data.msg}
					details={data.details}
				/>
			);
		} else {
			toast(<ToastDisplay status="success" content={data.msg} />);
			closeUserModal();
			navigate(`/users/${data.user_id}`);
		}
		setActionLoading(false);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		setActionLoading(true);
		var newErrors = [];

		requiredFields.map((field) => {
			if (!field.value || field.value === "" || field.value.length === 0) {
				newErrors.push(field.key);
			}
		});

		if (newErrors.length !== 0) {
			toast(
				<ToastDisplay
					status="error"
					content="Please fill out all required fields."
				/>
			);
			setActionLoading(false);
			setErrors(newErrors);
			return;
		}
		saveUser();
	};

	useEffect(() => {
		document.title = "Users | Pisara Dashboard";
		if (profile) {
			if (!profile.role.users_view) {
				navigate("/");
			} else {
				getData();
				if (filterFields.every((field) => isEmpty(field.value))) {
					setShowFilters(false);
				} else {
					setShowFilters(true);
				}
			}
		}
	}, [searchParams, profile]);

	return (
		<>
			<Container className="gx-5">
				<Row className="content-header flex-row-left mb-20">
					<Col>
						<h1 className="text-xl m-0">Users</h1>
					</Col>
				</Row>
				<Row className="flex-row-left mb-20">
					<Col className="col-5">
						<Form onSubmit={search}>
							<div className="search-container bg-white flex-row-left">
								<i className="ri-search-line search-icon" />
								<Form.Control
									className="text-sm"
									placeholder="Search users"
									value={searchQ}
									onChange={(e) => setSearchQ(e.target.value)}
								/>
								<Button
									variant="light"
									className="filter-btn flex-column"
									onClick={() => setFilterModal(true)}
								>
									<i className="ri-equalizer-3-fill m-0" />
								</Button>
							</div>
						</Form>
					</Col>
					<Col className="col-7 flex-row-right">
						<Pagination
							meta={meta}
							searchParams={searchParams}
							setSearchParams={setSearchParams}
							searchFields={searchFields}
							className="pull-right flex-row-left"
						/>
						<Button
							variant="light"
							className={`refresh-btn flex-column ${
								!profile?.role?.users_write && "no-margin"
							}`}
							onClick={getData}
						>
							<i className="ri-restart-line opacity-7" />
						</Button>
						{profile?.role?.users_write && (
							<Button
								className="control-btn btn-sm btn-default text-medium-gotham text-sm"
								onClick={() => setUserModal(true)}
							>
								<i className="ri-user-add-line" />
								Add User
							</Button>
						)}
					</Col>
				</Row>
				<Row className="content-table-header bg-white gx-0">
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">Name</span>
					</Col>
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">
							Email
						</span>
					</Col>
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">Role</span>
					</Col>
					<Col className="p-10">
						<span className="text-sm text-medium-gotham mb-10 block">
							Status
						</span>
					</Col>
				</Row>
				{loading ? (
					<PageLoader className="bg-white p-50" />
				) : users?.length === 0 ? (
					<Empty label="No users to show." className="bg-white" />
				) : (
					users.map((user) => (
						<Link key={user.id} to={`/users/${user.id}`}>
							<Row className="content-table-item bg-white gx-0">
								<Col className="p-10 ellipsis">
									<span className="text-sm">{user.name}</span>
								</Col>
								<Col className="p-10 ellipsis">
									<span className="text-sm">{user.email}</span>
								</Col>
								<Col className="p-10 ellipsis">
									<span className="text-sm">{user.role?.name}</span>
								</Col>
								<Col className="p-10 ellipsis">
									<span className="text-sm">{user.status}</span>
								</Col>
							</Row>
						</Link>
					))
				)}
			</Container>
			<Modal
				show={userModal}
				className="admin-content"
				centered
				size="lg"
				onHide={closeUserModal}
			>
				<Form onSubmit={handleSubmit}>
					<Modal.Header>
						<Modal.Title className="text-lg">Add User</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<div className="mb-10">
							<h2 className="text-lg text-medium-gotham mb-0">
								User Information
							</h2>
						</div>
						<Row className="mb-30">
							<Col>
								<Form.Label className="text-sm">Name</Form.Label>
								<Form.Control
									autoFocus
									className={`text-sm form-input ${
										errors.includes("name") && "border-danger"
									}`}
									value={name}
									onChange={(e) => setName(e.target.value)}
								/>
							</Col>
						</Row>
						<Row className="mb-30">
							<Col>
								<Form.Label className="text-sm">Email</Form.Label>
								<Form.Control
									type="email"
									className={`text-sm form-input ${
										errors.includes("email") && "border-danger"
									}`}
									value={email}
									onChange={(e) => setEmail(e.target.value)}
								/>
							</Col>
							<Col>
								<Form.Label className="text-sm">Role</Form.Label>
								<Form.Select
									className={`text-sm form-input ${
										errors.includes("role") && "border-danger"
									}`}
									value={role}
									onChange={(e) => setRole(e.target.value)}
								>
									<option value=""></option>
									{roles?.map((role) => (
										<option value={role.id}>{role.name}</option>
									))}
								</Form.Select>
							</Col>
						</Row>
						<div>
							<p className="label-md italic">
								We'll send them an email with the invite link.
							</p>
						</div>
					</Modal.Body>
					<Modal.Footer>
						<Button
							variant="outline-secondary"
							className="btn-sm action-btn text-sm text-medium-inter me-2"
							onClick={closeUserModal}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							className="btn-default btn-sm action-btn flex-column text-sm text-medium-inter"
							disabled={actionLoading}
						>
							{actionLoading ? (
								<Spinner className="btn-loader text-white" />
							) : (
								"Invite"
							)}
						</Button>
					</Modal.Footer>
				</Form>
			</Modal>
		</>
	);
};

export default Users;
